// src/shared/modal.js — модалните прозорци (Фаза 3.3: вече истинската
// имплементация, пренесена от app.js; дотогава беше мост).
// escClose е синглетон на ниво модул; registry-autofill.js наблюдава
// #modal-root и структурата .modal/.modal-head/.modal-body — тя е контракт.

import { el, $ } from './dom.js';
import { isReadOnlyView } from './state.js';

// Достъпност на модала (виж и actionsMenu в ui.js — същият модел):
//  • role="dialog" + aria-modal + име от заглавието — четецът разбира, че се
//    е отворил прозорец;
//  • фокусът ВЛИЗА в прозореца и Tab обикаля вътре — иначе клавиатурата
//    излиза зад затъмнението, при бутони, които потребителят не вижда;
//  • фонът става inert — не се фокусира и не се чете;
//  • при затваряне фокусът СЕ ВРЪЩА на бутона, който е отворил прозореца.
let modalSeq = 0;
const returnFocus = [];  // стек: вложените модали се затварят отвътре навън

export function openModal({ title, body, foot, wide, narrow, dirtyCheck, onDismiss, allowEdit }) {
  returnFocus.push(document.activeElement);
  const titleId = 'modal-title-' + (++modalSeq);

  const overlay = el('div', {
    class: 'overlay', tabindex: '-1',
    role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId,
    // Широките форми (фактура, фирма, протокол) НЕ се затварят с клик по
    // фона — там той е тесен ръб и се улучва неволно (решение на Макс,
    // Фаза А.1). Изходите им са Esc и „×", с въпрос при незаписани промени.
    onClick: (e) => { if (e.target === overlay && !wide) requestClose(overlay); },
  },
    // narrow — кратките въпроси „Да/Не" (confirmDialog): след разширяването
    // на прозорците (т. 2) един ред текст в 760 px изглеждаше изгубен.
    el('div', { class: 'modal' + (wide ? ' wide' : (narrow ? ' narrow' : '')) },
      el('div', { class: 'modal-head' },
        el('h3', { id: titleId, text: title }),
        el('button', { class: 'x', type: 'button', 'aria-label': 'Затвори', onClick: () => requestClose(overlay) },
          el('span', { 'aria-hidden': 'true', text: '×' })
        )
      ),
      el('div', { class: 'modal-body' }, body),
      foot ? el('div', { class: 'modal-foot' }, foot) : null
    )
  );
  // Незаписана работа: писане в ТЯЛОТО бележи прозореца като „пипнат" и
  // потребителските изходи (Esc, фон, „×") минават през въпрос — виж
  // requestClose. НАРОЧНО само .modal-body: действията в .modal-foot (смяна
  // на статус, PDF) се записват веднага и не са „незаписани промени".
  // Програмното closeModal() — пътят на „Запази" — не пита никога.
  // dirtyCheck дава на формата собствена преценка (бие автоматичната);
  // onDismiss се вика при потребителско затваряне (ползва го confirmDialog,
  // за да не увисне обещанието му при Esc върху въпроса).
  // „Само преглед" (грешка от картата, 16.10.2026): прозорец, отворен от
  // раздел с право само за четене, показва записа, но полетата са
  // неактивни, а бутоните за промяна (плътните и червените) ги няма.
  // Въпросите „Да/Не" (narrow) и изрично разрешените (allowEdit) — не.
  if (!narrow && !allowEdit && isReadOnlyView()) lockReadOnly(overlay);
  overlay._dirty = false;
  overlay._dirtyCheck = dirtyCheck || null;
  overlay._onDismiss = onDismiss || null;
  const bodyBox = overlay.querySelector('.modal-body');
  const mark = () => { overlay._dirty = true; };
  bodyBox.addEventListener('input', mark);
  bodyBox.addEventListener('change', mark);
  // Телефон (ПРОМТ-37): клавиатурата излиза ~300 ms след докосването и
  // покрива долната половина — полето, в което се пише, се вдига в средата.
  bodyBox.addEventListener('focusin', (e) => {
    const t = e.target;
    if (!t || !/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
    if (!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches)) return;
    setTimeout(() => { if (document.activeElement === t && t.scrollIntoView) t.scrollIntoView({ block: 'center' }); }, 320);
  });
  $('#modal-root').appendChild(overlay);
  // Фонът престава да съществува за клавиатурата и екранните четци.
  const app = $('#app');
  if (app) app.setAttribute('inert', '');
  document.addEventListener('keydown', escClose);
  overlay.addEventListener('keydown', trapTab);

  // Фокусът влиза вътре. Търсим ПЪРВОТО ПОЛЕ в тялото, а не първото
  // фокусируемо изобщо — иначе фокусът пада на „×" горе вдясно (той е пръв
  // в реда на документа) и четецът обявява „Затвори", вместо да въведе
  // човека във формата.
  const bodyEl = overlay.querySelector('.modal-body');
  const field = bodyEl && [...bodyEl.querySelectorAll('input,select,textarea')]
    .find((n) => !n.disabled && !n.readOnly && !hidden(n));
  (field || focusables(overlay)[0] || overlay).focus();

  return overlay;
}

function lockReadOnly(overlay) {
  const bodyBox = overlay.querySelector('.modal-body');
  if (bodyBox) {
    bodyBox.querySelectorAll('input, select, textarea').forEach((n) => { n.disabled = true; });
    bodyBox.querySelectorAll('button.btn:not(.ghost), button.btn.danger').forEach((n) => { n.hidden = true; });
    bodyBox.insertBefore(el('div', { class: 'readonly-banner', role: 'status', text: '👁 Само преглед — промените не могат да се запишат.' }), bodyBox.firstChild);
  }
  overlay.querySelectorAll('.modal-foot button.btn:not(.ghost), .modal-foot button.btn.danger, .modal-foot select').forEach((n) => { n.hidden = true; });
}

export function closeModal() {
  const root = $('#modal-root');
  if (root.lastChild) root.removeChild(root.lastChild); // затваря само най-горния модал
  if (!root.firstChild) {
    document.removeEventListener('keydown', escClose);
    const app = $('#app');
    if (app) app.removeAttribute('inert');
  }
  // Фокусът се връща откъдето е дошъл (ако елементът още е на страницата).
  const back = returnFocus.pop();
  if (back && back.isConnected && typeof back.focus === 'function') back.focus();
}

function escClose(e) { if (e.key === 'Escape') requestClose($('#modal-root').lastChild); }

// Потребителско затваряне (Esc, клик по фона, „×"): при незаписани промени
// пита, вместо да изхвърли работата. Върхът на стека пита за СЕБЕ СИ —
// потвърждението (само̀ чисто) при Esc просто се затваря и формата остава.
function isDirty(overlay) {
  if (overlay._dirtyCheck) { try { return !!overlay._dirtyCheck(); } catch (e) { return overlay._dirty; } }
  return overlay._dirty;
}
async function requestClose(overlay) {
  if (!overlay) return;
  if (!isDirty(overlay)) {
    if (overlay._onDismiss) overlay._onDismiss();
    closeModal();
    return;
  }
  const ok = await confirmDialog('Имате незаписани промени. Да затворя ли без запис?',
    { okText: 'Затвори без запис', cancelText: 'Продължи попълването', danger: true });
  if (ok) {
    if (overlay._onDismiss) overlay._onDismiss();
    closeModal();
  }
}

// Скрит ли е елементът. НЕ ползваме offsetParent: той зависи от подредбата
// и е null в тестова среда без изчисляване на разположение — тогава капанът
// за фокуса би останал празен и Tab пак щеше да излиза зад прозореца.
function hidden(n) {
  if (n.hidden || n.type === 'hidden') return true;
  const cs = (n.ownerDocument.defaultView || window).getComputedStyle(n);
  return cs.display === 'none' || cs.visibility === 'hidden';
}

function focusables(scope) {
  return [...scope.querySelectorAll('a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])')]
    .filter((n) => !n.disabled && !hidden(n));
}

// Tab обикаля В прозореца, вместо да излиза зад него.
function trapTab(e) {
  if (e.key !== 'Tab') return;
  const items = focusables(e.currentTarget);
  if (!items.length) return;
  const first = items[0], last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

// Снимка на най-горната „пипната" форма — за черновата при авто-изход (А.5).
// Пароли и скрити полета не влизат. Стойностите са за ПОКАЗВАНЕ (човек ги
// преписва), не за машинно възстановяване — формите се строят по запис и
// сляпото наливане обратно би лъгало при редове, дати и падащи списъци.
export function modalDraft() {
  const root = $('#modal-root');
  const ov = root ? [...root.children].reverse().find((o) => isDirty(o)) : null;
  if (!ov) return null;
  const head = ov.querySelector('.modal-head h3');
  const title = (head && head.textContent) || 'Форма';
  const fields = [];
  ov.querySelectorAll('.modal-body input, .modal-body select, .modal-body textarea').forEach((n) => {
    if (n.type === 'password' || n.type === 'hidden') return;
    let value = '';
    if (n.type === 'checkbox') value = n.checked ? 'да' : '';
    else if (n.tagName === 'SELECT') value = (n.selectedOptions && n.selectedOptions[0] && n.selectedOptions[0].textContent) || '';
    else value = n.value;
    if (!String(value).trim()) return;
    const lbl = n.id ? ov.querySelector('label[for="' + n.id + '"]') : null;
    fields.push({ label: (lbl && lbl.textContent) || n.placeholder || '', value: String(value) });
  });
  return fields.length ? { title, fields } : null;
}

// Потвърждение преди действие.
//   confirmDialog('Да продължа ли?')                              → „Потвърди"
//   confirmDialog('Да сменя ли статуса?', { okText: 'Промени' })   → неутрален бутон
//   confirmDialog('Да изтрия ли?', { okText: 'Изтрий', danger: true }) → червен бутон
// НАРОЧНО без разрушителна подразбираща стойност: бутонът е червен и пише
// „Изтрий" САМО когато повикващият изрично го поиска.
export function confirmDialog(message, opts = {}) {
  const o = typeof opts === 'string' ? { okText: opts } : (opts || {});
  const okText = o.okText || 'Потвърди';
  const cancelText = o.cancelText || 'Отказ';
  const danger = !!o.danger;
  return new Promise((resolve) => {
    const foot = el('div', {},
      el('button', { class: 'btn ghost', text: cancelText, onClick: () => { closeModal(); resolve(false); } }),
      el('button', { class: 'btn' + (danger ? ' danger' : ''), text: okText, onClick: () => { closeModal(); resolve(true); }, style: 'margin-left:8px' })
    );
    // onDismiss: Esc/фон върху въпроса = отказ. Без това обещанието виси
    // неразрешено и повикващият (напр. requestClose) чака вечно.
    openModal({ title: 'Потвърждение', body: el('p', { text: message, style: 'margin:0' }), foot, narrow: true, onDismiss: () => resolve(false) });
  });
}
